/**
 * useWebSocket Hook - React hook for WebSocket
 *
 * Usage:
 *   const { subscribe, on, off } = useWebSocket();
 *
 *   useEffect(() => {
 *     subscribe('social');
 *     const unsubscribe = on('new_post', (event) => {
 *       console.log('New post:', event.data);
 *     });
 *     return unsubscribe;
 *   }, []);
 */

import { useCallback } from 'react';
import {
    WebSocketChannel,
    WebSocketEventHandler,
    WebSocketEventType,
    websocketService
} from '../services/websocket';

export function useWebSocket() {
  /**
   * Connect to WebSocket server
   */
  const connect = useCallback((userId: number): Promise<void> => {
    return websocketService.connect(userId);
  }, []);

  /**
   * Disconnect from WebSocket server
   */
  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  /**
   * Subscribe to a channel
   */
  const subscribe = useCallback((channel: WebSocketChannel) => {
    websocketService.subscribe(channel);
  }, []);

  /**
   * Unsubscribe from a channel
   */
  const unsubscribe = useCallback((channel: WebSocketChannel) => {
    websocketService.unsubscribe(channel);
  }, []);

  /**
   * Register event handler
   */
  const on = useCallback((eventType: WebSocketEventType, handler: WebSocketEventHandler) => {
    return websocketService.on(eventType, handler);
  }, []);

  /**
   * Remove event handler
   */
  const off = useCallback((eventType: WebSocketEventType, handler: WebSocketEventHandler) => {
    websocketService.off(eventType, handler);
  }, []);

  /**
   * Check if connected
   */
  const isConnected = useCallback(() => {
    return websocketService.isConnected();
  }, []);

  return {
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    on,
    off,
    isConnected
  };
}
